import type { Client } from '../../api/client.js';
import type { VideoPortail } from './composants.js';
import {
  projeterAccueil,
  projeterConfidentialite,
  type AccueilPortail,
  type ConfidentialiteLue,
  type CorpsModification,
} from './modele.js';

/** Routes du contrat que les portails client appellent. Aucune route d'équipe : le portail ne lit que sa propre branche. */
export const ROUTES_DU_PORTAIL = [
  'portail.accueil',
  'portail.journal',
  'portail.videos',
  'portail.confidentialite',
  'portail.confidentialite.modifier',
  'portail.pieces.demander',
  'confidentialite.demandes',
] as const;

export interface EntreeJournal {
  at: string;
  kind: string;
  summary: string;
}

export type GenreDemande = 'acces' | 'suppression';

export interface SourcePortail {
  chargerAccueil: () => Promise<AccueilPortail>;
  chargerJournal: () => Promise<EntreeJournal[]>;
  /** Vidéos partagées par le cabinet (PARITE-5) : titre et lecteur seulement. */
  chargerVideos: () => Promise<VideoPortail[]>;
  chargerConfidentialite: () => Promise<ConfidentialiteLue>;
  enregistrerChoix: (corps: CorpsModification) => Promise<ConfidentialiteLue>;
  demanderDonnees: (genre: GenreDemande) => Promise<void>;
  /** « Je ne l'ai pas : demandez-le pour moi » : le cabinet demande lui-même la pièce attendue. */
  demanderPiece: (requirementId: string) => Promise<void>;
}

/**
 * Accès aux routes du portail. Toute lecture est projetée sur une liste blanche (`modele.ts`) ; toute écriture passe par une
 * route auditée du contrat (le client ajoute la clé d'idempotence). Une erreur du serveur reste une `ErreurApi` : sa phrase
 * française est la seule chose qui atteint l'écran.
 */
export function creerSource(client: Client): SourcePortail {
  return {
    async chargerAccueil() {
      return projeterAccueil(await client.appeler<unknown>('portail.accueil'));
    },
    async chargerJournal() {
      const lu = await client.appeler<EntreeJournal[]>('portail.journal');
      return lu.map((e) => ({ at: e.at, kind: e.kind, summary: e.summary }));
    },
    async chargerVideos() {
      const lu = await client.appeler<VideoPortail[]>('portail.videos');
      return Array.isArray(lu) ? lu.map((v) => ({ id: v.id, title: v.title, embedUrl: v.embedUrl })) : [];
    },
    async chargerConfidentialite() {
      return projeterConfidentialite(await client.appeler<unknown>('portail.confidentialite'));
    },
    async enregistrerChoix(corps) {
      return projeterConfidentialite(await client.appeler<unknown>('portail.confidentialite.modifier', { corps }));
    },
    async demanderDonnees(genre) {
      await client.appeler('confidentialite.demandes', { corps: { kind: genre } });
    },
    async demanderPiece(requirementId) {
      await client.appeler('portail.pieces.demander', { corps: { requirementId } });
    },
  };
}
